import React, { useState } from "react"
import * as Icons from "lucide-react"

const FIELDS = [
  { value: "from", label: "From (sender) contains" },
  { value: "subject", label: "Subject contains" },
  { value: "body", label: "Message body contains" },
]
const ACTIONS = [
  { value: "flag", label: "Flag it (Follow Up)" },
  { value: "star", label: "Star it" },
  { value: "archive", label: "Move to Archive" },
  { value: "delete", label: "Delete it" },
]

function newId() {
  return (
    "r" + Date.now().toString(36) + Math.floor(Math.random() * 1e4).toString(36)
  )
}

/* Outlook-style Rules manager (client-side, persisted by the parent). */
export default function EmailRules({
  rules = [],
  onSave,
  onRunRules,
  onClose,
}) {
  const [list, setList] = useState(rules)
  const [name, setName] = useState("")
  const [field, setField] = useState("from")
  const [value, setValue] = useState("")
  const [action, setAction] = useState("flag")

  const persist = next => {
    setList(next)
    onSave(next)
  }

  const addRule = () => {
    if (!value.trim()) return
    const rule = {
      id: newId(),
      name:
        name.trim() ||
        `${FIELDS.find(f => f.value === field)?.label} "${value.trim()}"`,
      field,
      value: value.trim(),
      action,
      enabled: true,
    }
    persist([...list, rule])
    setName("")
    setValue("")
  }

  const toggle = id =>
    persist(list.map(r => (r.id === id ? { ...r, enabled: !r.enabled } : r)))
  const remove = id => persist(list.filter(r => r.id !== id))

  const actionLabel = a => ACTIONS.find(x => x.value === a)?.label || a
  const fieldLabel = f => FIELDS.find(x => x.value === f)?.label || f

  return (
    <div className="em-search-overlay" onClick={onClose}>
      <div className="em-rules" onClick={e => e.stopPropagation()}>
        <div className="em-rules-head">
          <div className="em-rules-title">
            <Icons.ListChecks size={18} /> Rules and Alerts
          </div>
          <button className="em-rules-x" onClick={onClose}>
            <Icons.X size={18} />
          </button>
        </div>

        <div className="em-rules-body">
          <p className="em-rules-hint">
            Rules run automatically on your loaded mail when you choose{" "}
            <strong>Run Rules Now</strong>, and help you organise messages the
            way Outlook does.
          </p>

          {/* Existing rules */}
          <div className="em-rules-list">
            {list.length === 0 && (
              <div className="em-rules-empty">
                No rules yet. Create one below.
              </div>
            )}
            {list.map(r => (
              <div
                key={r.id}
                className={`em-rules-item ${
                  r.enabled ? "" : "em-rules-item-off"
                }`}
              >
                <label
                  className="em-rules-toggle"
                  title={r.enabled ? "Enabled" : "Disabled"}
                >
                  <input
                    type="checkbox"
                    checked={r.enabled}
                    onChange={() => toggle(r.id)}
                  />
                  <span className="em-rules-check" />
                </label>
                <div className="em-rules-item-text">
                  <div className="em-rules-item-name">{r.name}</div>
                  <div className="em-rules-item-desc">
                    When <strong>{fieldLabel(r.field)}</strong> “{r.value}” →{" "}
                    <strong>{actionLabel(r.action)}</strong>
                  </div>
                </div>
                <button
                  className="em-rules-del"
                  onClick={() => remove(r.id)}
                  title="Delete rule"
                >
                  <Icons.Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>

          {/* New rule builder */}
          <div className="em-rules-new">
            <div className="em-rules-new-title">Create a new rule</div>
            <input
              className="em-rules-input"
              placeholder="Rule name (optional)"
              value={name}
              onChange={e => setName(e.target.value)}
            />
            <div className="em-rules-row">
              <span className="em-rules-lbl">When</span>
              <select
                className="em-rules-select"
                value={field}
                onChange={e => setField(e.target.value)}
              >
                {FIELDS.map(f => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
              <input
                className="em-rules-input em-rules-input-value"
                placeholder="text to match…"
                value={value}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") addRule()
                }}
              />
            </div>
            <div className="em-rules-row">
              <span className="em-rules-lbl">Do this</span>
              <select
                className="em-rules-select"
                value={action}
                onChange={e => setAction(e.target.value)}
              >
                {ACTIONS.map(a => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
              <button
                className="em-rules-add"
                onClick={addRule}
                disabled={!value.trim()}
              >
                <Icons.Plus size={15} /> Add rule
              </button>
            </div>
          </div>
        </div>

        <div className="em-rules-foot">
          <button className="em-rules-run" onClick={() => onRunRules(list)}>
            <Icons.Play size={15} /> Run Rules Now
          </button>
          <button className="em-rules-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
